import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { GAMES, PACKAGES, PRODUCTS } from '../content'

export const Route = createFileRoute('/projects')({
  head: () => ({
    meta: [
      { title: 'projects - jfrader' },
      { name: 'description', content: 'projects, published packages and open source by jfrader.' },
      { property: 'og:title', content: 'projects - jfrader' },
      { property: 'og:description', content: 'projects, published packages and open source by jfrader.' },
      { property: 'og:url', content: 'https://jfrader.com/projects' },
    ],
    links: [
      { rel: 'canonical', href: 'https://jfrader.com/projects' }
    ]
  }),
  component: ProjectsPage,
})

type Repo = {
  name: string
  htmlUrl: string
  description: string
  stars: number
  pushedAt: string | null
}

type Projects = { repos: Repo[]; degraded: boolean }

/* The live list is the API's. The build also ships a snapshot beside the HTML,
   which this falls back to when the API is unreachable or answers degraded and
   empty, so an exhausted GitHub quota or a cold cache never leaves the page
   with nothing to show. The snapshot uses the same fetch and ordering. */
async function fetchProjects(): Promise<Projects> {
  try {
    const res = await fetch('/api/v1/projects', { headers: { Accept: 'application/json' } })
    if (res.ok) {
      const live = (await res.json()) as Projects
      if (Array.isArray(live.repos) && live.repos.length > 0) return live
    }
  } catch {
    // fall through to the snapshot below
  }

  const res = await fetch('/projects-snapshot.json', { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error('Failed to load projects')
  // The payload says degraded: false because it was live when the build fetched
  // it. From here it is a snapshot, so say so.
  return { ...((await res.json()) as Projects), degraded: true }
}

/* The order and the selection come from the API, which sorts by stars and then
   by how recently a repository was pushed. Curated entries declare the
   repositories they are built from, so the live list leaves those out instead
   of showing the same work twice. */
const FEATURED_REPOS = new Set([
  ...PACKAGES.map((p) => p.repo),
  ...PRODUCTS.map((p) => p.repo),
  ...GAMES.flatMap((g) => g.repos ?? []),
])

/* One formatter for the whole list: the wording, the plural and "yesterday"
   are the platform's job, not a table of suffixes in here. */
const AGO = new Intl.RelativeTimeFormat('en', { numeric: 'auto', style: 'long' })
const DAY_MS = 24 * 60 * 60 * 1000

/** The absolute date, for the tooltip and for anyone who needs the real thing. */
const EXACT = new Intl.DateTimeFormat('en', { dateStyle: 'medium' })

function exact(iso: string | null): string | undefined {
  const time = iso ? Date.parse(iso) : NaN
  return Number.isFinite(time) ? EXACT.format(time) : undefined
}

/** How long ago a repository was last pushed: "yesterday", "in 3 days". */
export function pushedAgo(iso: string | null, now = Date.now()): string {
  const time = iso ? Date.parse(iso) : NaN
  if (!Number.isFinite(time)) return ''

  /* Rounded, not floored: 23 hours is "today", 25 hours is "yesterday", and a
     timestamp in the future says so instead of claiming it happened today. */
  const days = Math.round((time - now) / DAY_MS)
  const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
    Math.abs(days) < 30
      ? [days, 'day']
      : Math.abs(days) < 365
        ? [Math.round(days / 30), 'month']
        : [Math.round(days / 365), 'year']

  return AGO.format(value, unit)
}

function ProjectsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: fetchProjects,
    staleTime: 1000 * 60 * 10,
    // one retry: the fail-soft line should appear in seconds, not after the
    // default three retries with backoff
    retry: 1,
  })

  const repos = (data?.repos ?? []).filter((r) => !FEATURED_REPOS.has(r.name))

  return (
    <>
      <section className="sec" id="sec-oss">
        <h2 className="sec__label">open source</h2>
        {isLoading && <p className="muted">Loading…</p>}
        {error && <p className="muted">Could not load live star counts.</p>}
        <div className="oss">
          {repos.map((r) => (
            <a className="osrow rv" key={r.name} href={r.htmlUrl} target="_blank" rel="noreferrer">
              <span className="osstars">{r.stars > 0 ? `${r.stars}★` : '·'}</span>
              <span className="osname">{r.name}</span>
              <span className="osdesc">{r.description}</span>
              <time className="osage" dateTime={r.pushedAt ?? undefined} title={exact(r.pushedAt)}>
                {pushedAgo(r.pushedAt)}
              </time>
            </a>
          ))}
          <a className="osrow rv" href="https://github.com/jfrader" target="_blank" rel="noreferrer">
            <span className="osstars">↗</span>
            <span className="osname">all repositories</span>
            <span className="osdesc">github.com/jfrader</span>
          </a>
        </div>
      </section>

      <section className="sec">
        <h2 className="sec__label">packages</h2>
        <div className="pkgs">
          {PACKAGES.map((p) => (
            <article className="pkg rv" key={p.name}>
              <code>{p.name}</code>
              <p>{p.desc}</p>
              <a href={p.url} target="_blank" rel="noreferrer">
                npm ↗
              </a>
            </article>
          ))}
        </div>
      </section>
    </>
  )
}
